import { Alert, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getEngagementSettings, updateEngagementSettings } from 'API/engagement';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EngagementSettings } from 'types/engagement.types';
import { QueryState, SectionCard } from '../../shared/ui';
import { settingsValid } from '../engagement-logic';

const DEFAULTS: EngagementSettings = {
  timeSaved: { profileReviewMin: 2, connectionRequestMin: 1.5, followUpMin: 1.5, inmailMin: 3 },
  caps: { emailPerDay: 1, emailPerWeek: 3, notificationPerDay: 2, cooldownHours: 48 },
  sunsetAfterUnopened: 5,
};

const SwitchChip: React.FC<{ label: string; value?: boolean }> = ({ label, value }) => {
  const { t } = useTranslation();
  return (
    <Chip
      label={`${label}: ${value === undefined ? t('engSwitchUnknown') : value ? t('engSwitchOn') : t('engSwitchOff')}`}
      color={value === undefined ? 'default' : value ? 'success' : 'warning'}
      variant={'outlined'}
    />
  );
};

const SettingsTab: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['engagement', 'settings'], queryFn: getEngagementSettings });
  const [s, setS] = useState<EngagementSettings>(DEFAULTS);
  const [saved, setSaved] = useState<'ok' | 'error' | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setS({ ...DEFAULTS, ...q.data, timeSaved: { ...DEFAULTS.timeSaved, ...q.data.timeSaved }, caps: { ...DEFAULTS.caps, ...q.data.caps } });
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => updateEngagementSettings({ timeSaved: s.timeSaved, caps: s.caps, sunsetAfterUnopened: s.sunsetAfterUnopened }),
    onSuccess: () => {
      setSaved('ok');
      queryClient.invalidateQueries({ queryKey: ['engagement', 'settings'] });
    },
    onError: () => setSaved('error'),
  });

  const num = (v: string) => (v === '' ? NaN : Number(v.replace(',', '.')));
  const field = (label: string, value: number, onChange: (n: number) => void, step = 1) => (
    <TextField
      size={'small'}
      type={'number'}
      label={label}
      value={Number.isNaN(value) ? '' : value}
      error={!Number.isFinite(value) || value < 0}
      inputProps={{ step, min: 0 }}
      onChange={(e) => {
        setSaved(null);
        onChange(num(e.target.value));
      }}
      sx={{ width: 220 }}
    />
  );

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Stack gap={2.5}>
        <SectionCard title={t('engSettingsSwitches')}>
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            <SwitchChip label={t('engSwitchSend')} value={q.data?.flags?.sendEnabled} />
            <SwitchChip label={t('engSwitchAutomations')} value={q.data?.flags?.automationsEnabled} />
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{t('engSettingsSwitchesNote')}</Typography>
        </SectionCard>
        <SectionCard title={t('engSettingsTimeSaved')}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>{t('engSettingsTimeSavedNote')}</Typography>
          <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
            {field(t('engTimeProfileReview'), s.timeSaved.profileReviewMin, (n) => setS({ ...s, timeSaved: { ...s.timeSaved, profileReviewMin: n } }), 0.5)}
            {field(t('engTimeConnectionRequest'), s.timeSaved.connectionRequestMin, (n) => setS({ ...s, timeSaved: { ...s.timeSaved, connectionRequestMin: n } }), 0.5)}
            {field(t('engTimeFollowUp'), s.timeSaved.followUpMin, (n) => setS({ ...s, timeSaved: { ...s.timeSaved, followUpMin: n } }), 0.5)}
            {field(t('engTimeInmail'), s.timeSaved.inmailMin, (n) => setS({ ...s, timeSaved: { ...s.timeSaved, inmailMin: n } }), 0.5)}
          </Stack>
        </SectionCard>
        <SectionCard title={t('engSettingsCaps')}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>{t('engSettingsCapsNote')}</Typography>
          <Stack direction={'row'} gap={1.5} flexWrap={'wrap'}>
            {field(t('engCapEmailPerDay'), s.caps.emailPerDay, (n) => setS({ ...s, caps: { ...s.caps, emailPerDay: n } }))}
            {field(t('engCapEmailPerWeek'), s.caps.emailPerWeek, (n) => setS({ ...s, caps: { ...s.caps, emailPerWeek: n } }))}
            {field(t('engCapNotificationPerDay'), s.caps.notificationPerDay, (n) => setS({ ...s, caps: { ...s.caps, notificationPerDay: n } }))}
            {field(t('engCapCooldownHours'), s.caps.cooldownHours, (n) => setS({ ...s, caps: { ...s.caps, cooldownHours: n } }))}
            {field(t('engSunsetAfter'), s.sunsetAfterUnopened, (n) => setS({ ...s, sunsetAfterUnopened: n }))}
          </Stack>
        </SectionCard>
        <Stack direction={'row'} gap={1.5} alignItems={'center'}>
          <Button variant={'contained'} disabled={!settingsValid(s) || save.isPending} onClick={() => save.mutate()}>
            {t('engSave')}
          </Button>
          {saved === 'ok' ? <Alert severity={'success'}>{t('engSettingsSaved')}</Alert> : null}
          {saved === 'error' ? <Alert severity={'error'}>{(save.error as Error)?.message ?? t('engComposeErrorGeneric')}</Alert> : null}
        </Stack>
      </Stack>
    </QueryState>
  );
};

export default SettingsTab;
